import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Bed, Search, User } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const RoomSelectDialog = ({
  open,
  onOpenChange,
  bookings = [],
  selectedBooking,
  onSelectBooking
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Filter bookings by search
  const filteredBookings = bookings.filter(booking => {
    const query = searchQuery.toLowerCase();
    return (
      booking.room_number?.toLowerCase().includes(query) ||
      booking.guest_name?.toLowerCase().includes(query) ||
      booking.room_id?.toLowerCase().includes(query)
    );
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="room-select-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bed className="h-5 w-5 text-purple-600" />
            Select Room for Room Service
          </DialogTitle>
        </DialogHeader>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by room number or guest name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="room-search-input"
          />
        </div>

        {/* Rooms List */}
        <ScrollArea className="h-[400px]">
          {filteredBookings.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Bed className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p className="text-sm">
                {bookings.length === 0 
                  ? 'No checked-in guests found' 
                  : 'No rooms match your search'}
              </p>
            </div>
          ) : (
            <div className="space-y-2 p-1">
              {filteredBookings.map((booking) => {
                const isSelected = selectedBooking?.id === booking.id;
                
                return (
                  <div
                    key={booking.id}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      isSelected
                        ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                        : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50/50'
                    }`}
                    onClick={() => {
                      onSelectBooking(booking);
                      onOpenChange(false);
                    }}
                    data-testid={`room-option-${booking.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                          isSelected ? 'bg-purple-500 text-white' : 'bg-purple-100 text-purple-600'
                        }`}>
                          <Bed className="h-5 w-5" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-lg">
                              Room {booking.room_number || booking.room_id?.slice(-4)}
                            </span>
                            <Badge variant="outline" className="text-xs">
                              {booking.room_type || 'Standard'}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-1 text-sm text-gray-500">
                            <User className="h-3 w-3" />
                            <span>{booking.guest_name || 'Guest'}</span>
                          </div>
                        </div>
                      </div>
                      
                      {isSelected && (
                        <Badge className="bg-purple-500">Selected</Badge>
                      )}
                    </div>
                    
                    {booking.check_in_date && (
                      <div className="mt-2 text-xs text-gray-400">
                        Check-in: {new Date(booking.check_in_date).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RoomSelectDialog;
